import { useCallback, useEffect, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";

import type { TypePhoto } from "./types";

// FR-4 §"Gallery" — desktop: main image + thumbnail strip; mobile:
// swipeable carousel with dot indicators. Both support keyboard arrow
// nav; click opens a lightbox Dialog. Single-photo and zero-photo
// edge cases (FR Edge Cases) collapse the chrome appropriately.
export interface PhotoGalleryProps {
  photos: TypePhoto[];
  typeName: string;
}

export function PhotoGallery({ photos, typeName }: PhotoGalleryProps): JSX.Element {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  // Reset to the first photo when the underlying list changes — protects
  // the index when the admin removes the currently-active photo while
  // the page is open.
  useEffect(() => {
    setActive(0);
  }, [photos]);

  const next = useCallback(() => {
    setActive((i) => (photos.length === 0 ? 0 : (i + 1) % photos.length));
  }, [photos.length]);

  const prev = useCallback(() => {
    setActive((i) => (photos.length === 0 ? 0 : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "ArrowRight") next();
      else if (event.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (photos.length === 0) {
    return (
      <div
        role="img"
        aria-label={`${typeName} photo coming soon`}
        className="flex aspect-[4/3] w-full items-center justify-center rounded-md border border-zinc-200 bg-zinc-100 text-zinc-400"
      >
        <span className="text-3xl font-bold tracking-tight">JDR</span>
      </div>
    );
  }

  const current = photos[active] ?? photos[0];

  return (
    <div>
      <button
        type="button"
        onClick={() => setLightbox(true)}
        className="block w-full overflow-hidden rounded-md border border-zinc-200 bg-brand-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
        aria-label={`Open larger view of ${current.alt_text || typeName}`}
      >
        <img
          src={current.url}
          alt={current.alt_text ?? `${typeName} photo ${active + 1}`}
          loading="eager"
          decoding="async"
          // First image is the LCP candidate — `fetchPriority` is a
          // recent attribute supported by Chromium/Safari/Firefox 116+;
          // older browsers ignore the unknown attribute harmlessly.
          {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
          className="aspect-[4/3] w-full object-cover"
        />
      </button>

      {photos.length > 1 ? (
        <>
          <p className="sr-only" aria-live="polite">
            Image {active + 1} of {photos.length}
          </p>

          {/* Mobile dot indicators */}
          <div className="mt-2 flex items-center justify-center gap-1 md:hidden">
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Go to photo ${i + 1}`}
                className={cn(
                  "h-2 w-2 rounded-full transition-colors",
                  i === active ? "bg-brand-red" : "bg-zinc-300"
                )}
              />
            ))}
          </div>

          {/* Desktop thumbnail strip */}
          <ul className="mt-3 hidden gap-2 md:flex" role="tablist" aria-label={`${typeName} photos`}>
            {photos.map((p, i) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  aria-controls={`gallery-${p.id}`}
                  onClick={() => setActive(i)}
                  className={cn(
                    "block h-16 w-16 overflow-hidden rounded border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2",
                    i === active ? "border-brand-red" : "border-zinc-200"
                  )}
                >
                  <img
                    src={p.url}
                    alt={p.alt_text ?? `${typeName} photo ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </button>
              </li>
            ))}
          </ul>

          {/* Mobile prev/next swipe buttons (also reachable via keyboard) */}
          <div className="mt-2 flex items-center justify-between gap-2 md:hidden">
            <button
              type="button"
              onClick={prev}
              className="h-10 min-w-[44px] rounded-md border border-zinc-200 px-3 text-sm text-brand-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
              aria-label="Previous photo"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={next}
              className="h-10 min-w-[44px] rounded-md border border-zinc-200 px-3 text-sm text-brand-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
              aria-label="Next photo"
            >
              ›
            </button>
          </div>
        </>
      ) : null}

      {lightbox ? (
        <Dialog open onOpenChange={(o) => { if (!o) setLightbox(false); }}>
          <DialogContent className="max-w-3xl">
            <DialogTitle className="sr-only">
              {current.alt_text ?? `${typeName} photo ${active + 1}`}
            </DialogTitle>
            <img
              src={current.url}
              alt={current.alt_text ?? `${typeName} photo ${active + 1}`}
              className="max-h-[80vh] w-full object-contain"
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
